import React, { useEffect, useRef, useState } from 'react';
import { Layer, Map, Marker, Source } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './map.module.scss'
import { useNavigate, useSearchParams } from 'react-router-dom';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import FreehandMode from './components/FreeHandMode';
import * as turf from '@turf/turf';
import RoutingModal from './components/Routing';
import Pin from 'components/Pin';
import { store } from 'stores/Store';
import { observer } from 'mobx-react';
import SlideUpMenus from 'Modules/SlideUpMenus';
import { ReactComponent as LassoIcon } from 'assets/lasso.svg';
import { ReactComponent as LocationIcon } from 'assets/location.svg';
import { ReactComponent as ReturnIcon } from 'assets/return.svg';
import { ReactComponent as RoutesIcon } from 'assets/routes.svg';
import { ReactComponent as MarkerIcon } from 'assets/marker.svg';
import Button from 'components/Button/Button';
import useExternalApiService from 'helpers/ExternalApiService';
import { ReactComponent as IdentificationIcon } from 'assets/blueIdentificationCard.svg';
import { ReactComponent as NavigationArrowIcon } from 'assets/navigationArrowRight.svg';
import { ReactComponent as AddRouteIcon } from 'assets/addRoute.svg';
import RoutesTab from 'Modules/PractitionerBaseballCard/components/RoutesTab';
import { LoadingOverlay } from '@mantine/core';

const MapComponent = observer(
  () => {
    const mapRef = React.useRef<mapboxgl.Map | undefined>(undefined)
    const popupRef = React.useRef<any>()

    const [isPopupOpen, setIsPopupOpen] = React.useState(false);
    const [selectedPractitioner, setSelectedPractitioner] = React.useState<any>();
    const [currentLocation, setCurrentLocation] = React.useState<[number, number]>([-122.0000379, 37.8130633]);
    const [isRoutingModalOpen, setIsRoutingModalOpen] = React.useState(false);
    const [routeData, setRouteData] = React.useState<GeoJSON.Feature<GeoJSON.LineString> | null>(null);
    const [isSlideUpMenusOpen, setIsSlideUpMenusOpen] = React.useState(false);
    const [isLassoMode, setIsLassoMode] = React.useState(false);
    const [isMapLoaded, setIsMapLoaded] = React.useState(false);
    const [initialLatLon, setInitialLatLon] = React.useState<number[]>();
    const [focalZoom, setFocalZoom] = React.useState<number>(6);
    const [isPractitionerListLoading, setIsPractitionerListLoading] = useState(false);
    const [isGoRouteModalOpen, setIsGoRouteModalOpen] = React.useState(false);
    const [routePinsSource, setRoutePinsSource] = React.useState<GeoJSON.FeatureCollection<GeoJSON.Point, { id: string }>>();
    const [searchParams] = useSearchParams()


    const [practPins, setPractPins] = useState<{ i: number, l: number[] }[]>([]);
    const practPinsRef = useRef(practPins);
    const [loadedPractsIds, setLoadedPractsIds] = useState<number[]>([]);
    const loadedPractsIdsRef = useRef<number[]>(loadedPractsIds);
    const [, setFetchedPractsIds] = useState<number[]>([]);
    const [onViewPractsIds, setOnViewPractsIds] = useState<number[]>([]);

    // Add touch tracking to distinguish between tap and drag
    const [touchStart, setTouchStart] = React.useState<{ x: number; y: number; time: number } | null>(null);

    // Add draw ref to persist the draw instance
    const drawRef = React.useRef<any>(null);
    const geolocateControlRef = React.useRef<mapboxgl.GeolocateControl | null>(null);

    const setHasRouteChangedRef = React.useRef<((value: boolean) => void) | null>(null);

    const apiUrl = process.env.REACT_APP_EAI_URL;
    const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
    const { postData } = useExternalApiService(apiUrl);

    useEffect(() => {
      loadedPractsIdsRef.current = loadedPractsIds;
    }, [loadedPractsIds]);

    useEffect(() => {
      practPinsRef.current = practPins;
    }, [practPins]);

    const practitionersGeoJSON = () => {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: string; selected: boolean }>[] =
        practPins
          .map((p) => {
            const [lat, lon] = p.l
            return {
              type: 'Feature',
              properties: {
                id: p.i as any,
                selected: false,
                // selected: store.selectedPractitioners.some((sel) => sel.name === p.name),
              },
              geometry: {
                type: 'Point',
                coordinates: [lon, lat]
              }
            };
          });
      return {
        type: 'FeatureCollection',
        features
      } as GeoJSON.FeatureCollection<GeoJSON.Point, { id: string; selected: boolean }>;
    }

    const handleMultiplePractitionersSelection = (ids: number[] = []): Promise<any> => new Promise((resolve, reject) => {
      if (!ids || ids.length === 0) {
        resolve([]);
        return;
      }
      postData('/api/get_practitioners_by_ids', {
        "fmc_email": store.impersonatedUser,
        practitioner_ids: ids,
      }).then((r) => {
        if (r?.practitioners && r?.practitioners.length > 0) {
          store.setPractitionersFilteredByFMC([
            ...store.practitionersFilteredByFMC,
            ...(r?.practitioners || []),
          ]);
          setLoadedPractsIds((prev) => ([
            ...prev,
            ...(r.practitioners.map((p: any) => p.id)),
          ]));
          resolve(r.practitioners);
        }
        setIsPractitionerListLoading(false);
      }).catch((err) => {
        setIsPractitionerListLoading(false);
      });
    });

    const handlePractitionerSelection = (id: number): Promise<any> => new Promise((resolve, reject) => {
      const clickedPract = store.practitionersFilteredByFMC.find((p) => p.id === id);
      if (clickedPract) {
        resolve(clickedPract);
      } else {
        if (!loadedPractsIds.includes(id)) {
          setIsPractitionerListLoading(true);
          postData('/api/get_practitioners_by_ids', {
            "fmc_email": store.impersonatedUser,
            practitioner_ids: [id],
          }).then((r) => {
            if (r?.practitioners && r?.practitioners.length > 0) {
              store.setPractitionersFilteredByFMC([
                ...store.practitionersFilteredByFMC,
                ...(r?.practitioners || []),
              ]);
              resolve(r.practitioners[0]);
            }
            setIsPractitionerListLoading(false);
          }).catch((err) => {
            setIsPractitionerListLoading(false);
          });
        }
      }
    });


    // Add new function for start/end locations GeoJSON
    const startEndLocationsGeoJSON = () => {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: string; type: 'start' | 'end' }>[] = [];

      // Get start and end locations from selectedPractitioners
      store.selectedPractitioners.forEach((item, index) => {
        if (item && item.latitude && item.longitude) {
          if (item.isFirstLocation) {
            features.push({
              type: 'Feature',
              properties: {
                id: `start-${index}`,
                type: 'start'
              },
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(String(item.longitude)), parseFloat(String(item.latitude))]
              }
            });
          } else if (item.isLastLocation) {
            features.push({
              type: 'Feature',
              properties: {
                id: `end-${index}`,
                type: 'end'
              },
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(String(item.longitude)), parseFloat(String(item.latitude))]
              }
            });
          }
        }
      });

      return {
        type: 'FeatureCollection',
        features
      } as GeoJSON.FeatureCollection<GeoJSON.Point, { id: string; type: 'start' | 'end' }>;
    }

    useEffect(() => {
      if (loadedPractsIds.length > 0) {
        setFetchedPractsIds((prev) => {
          const practsIds = loadedPractsIds.filter((l) => !prev.includes(l))

          //TODO: error handling + retry
          if (practsIds.length === 0) {
            return prev;
          }

          postData('/api/get_practitioners_by_ids', {
            "fmc_email": store.impersonatedUser,
            practitioner_ids: practsIds,
          }).then((r) => {
            store.setPractitionersFilteredByFMC([
              ...store.practitionersFilteredByFMC,
              ...(r?.practitioners || []),
            ])
          });
          return ([
            ...prev,
            ...practsIds
          ]);
        })
      }
    }, [loadedPractsIds]);

    useEffect(() => {
      let tid: any;
      tid = setTimeout(() => {
        setLoadedPractsIds((prev) => ([
          ...prev,
          ...onViewPractsIds.filter((o) => !prev.includes(o))
        ]))
      }, 500);
      return () => {
        clearTimeout(tid);
      };
    }, [onViewPractsIds]);

    React.useEffect(() => {
      // fetchPractitioners();

      // Prevent pull-to-refresh behavior when lasso mode is active
      document.body.style.overscrollBehavior = 'none';
      if (mapRef.current) {
        mapRef.current.getCanvas().style.touchAction = 'none';
      }
    }, []);

    React.useEffect(() => {
      if (!isSlideUpMenusOpen) {
        setIsLassoMode(false);
      }
    }, [isSlideUpMenusOpen]);

    React.useEffect(() => {
      fetchPractitioners();
    }, [store.impersonatedUser])

    React.useEffect(() => {
      if (isRoutingModalOpen === true) {
        if (drawRef.current) {
          drawRef.current.deleteAll();
        }
      }
    }, [isRoutingModalOpen])

    // Initialize draw controls when map is ready
    React.useEffect(() => {
      if (mapRef.current && isMapLoaded && !drawRef.current) {
        mountMapboxDraw();

        // Add GeolocateControl once when map is loaded
        if (!geolocateControlRef.current) {
          geolocateControlRef.current = new mapboxgl.GeolocateControl({
            positionOptions: {
              enableHighAccuracy: true,
            },
            trackUserLocation: true,
            showUserHeading: true,
          });

          mapRef.current.addControl(geolocateControlRef.current, 'top-left');

          setTimeout(() => {
            const geolocateButton = document.querySelector('.mapboxgl-ctrl-geolocate');
            if (geolocateButton) {
              (geolocateButton as HTMLElement).style.display = 'none';
              (geolocateButton as HTMLElement).style.position = 'absolute';
            }
          }, 0);
        }
      }
    }, [mapRef.current, isMapLoaded]);

    React.useEffect(() => {
      if (store?.currentDirections) {
        if (store?.currentDirections.length > 0) {
          const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: store?.currentDirections
            }
          };
          setRouteData(geojson);
        } else {
          setRouteData(null);
        }
      } else {
        setRouteData(null);
      }
    }, [store.currentDirections]);

    React.useEffect(() => {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: string }>[] = [];

      store.selectedPractitioners
        .filter((s) => !s.isFirstLocation && !s.isLastLocation)
        .forEach((p) => {
          const lon = parseFloat(String(p.longitude));
          const lat = parseFloat(String(p.latitude));
          features.push({
            type: "Feature",
            properties: {
              id: p.id,
            },
            geometry: {
              type: 'Point',
              coordinates: [lon, lat]
            }
          })
        });

      const ds: GeoJSON.FeatureCollection<GeoJSON.Point, { id: string }> = {
        type: "FeatureCollection",
        features,
      }
      setRoutePinsSource(ds);
    }, [store.selectedPractitioners]);


    const navigate = useNavigate();
    const navigateHome = () => {
      navigate(`/`)
    }

    const fetchPractitioners = async () => {
      try {
        setIsPractitionerListLoading(true);
        const lat = searchParams.get("lat");
        const lon = searchParams.get("lon");
        const selectedPractitionerId = parseInt(searchParams.get("id") || "0");

        if (lat && lon) {
          setInitialLatLon([parseFloat(lat), parseFloat(lon)])
          setFocalZoom(15);
        } else {
          if (store.initialLatLon) {
            setInitialLatLon(store.initialLatLon);
          }
        }
        const data = await postData('/api/get_practitioners_for_chat_map',
          {
            "fmc_email": store.impersonatedUser,
            onlyGeoValidated: true,
            limit: 999999999,
            alias: store.impersonatedUserAlias,
          });
        setPractPins(data?.p || []);

        //console.log("Calling fetchPracitioners ");        

        const validPracts = data?.p.filter((p: any) => {
          return (
            p.l !== null &&
            p.l !== undefined &&
            p.l !== ""
          );
        });

        if (validPracts.length > 0 && !store.initialLatLon && (!lat && !lon)) {
          // 2) Take the first "valid" practitioner
          const firstValid = validPracts[0];

          // 3) Parse floats and set as initial target for flyTo
          const [parsedLat, parsedLng] = firstValid.l;
          setInitialLatLon([parsedLat, parsedLng])
          //console.log("→ flying to first‐valid practitioner:", parsedLng, parsedLat);

          // flyToLocation(parsedLng, parsedLat, 0);
        } else {
          console.warn("No practitioners with a valid lat/lng were found!");
          // Optionally, you could fall back to a default coordinate here
        }

        /*
        if (data.practitioners.length > 0) {
          const firstPractitioner = data.practitioners[0];
          console.log("latitude: ", firstPractitioner.latitude);
          console.log("long: ", firstPractitioner.longitude);
          if (firstPractitioner.longitude && firstPractitioner.latitude) {
            flyToLocation(
              parseFloat(firstPractitioner.longitude),
              parseFloat(firstPractitioner.latitude),
              0
            );
          }
        }
        */
        if (selectedPractitionerId) {
          handlePractitionerSelection(selectedPractitionerId)
            .then((p) => setSelectedPractitioner(p))
        }
      } catch (error) {
        console.error('Error fetching practitioners:', error);
      }

      setIsPractitionerListLoading(false);
    };

    // const flyToLocation = (long: number, lat: number, pitch: number) => {
    //   //console.log("Calling flyToLocation ");
    //   //console.log("latitude: ", lat);
    //   //console.log("long: ", long);
    //   if (mapRef.current !== undefined) {
    //     mapRef.current.flyTo({
    //       center: [long, lat],
    //       zoom: 7,
    //       speed: 2, // 0.5,
    //       curve: 1.5,
    //       bearing: 0,
    //       pitch: pitch,
    //       essential: true,
    //     })
    //   }
    // }

    const updateArea = (e: any) => {
      const data = drawRef.current.getAll();

      if (data.features.length > 0) {
        const polygon = data.features[0];
        let selectedPractitioners = [...store.selectedPractitioners];
        const originalLength = selectedPractitioners.length;
        const notLoadedPracts: number[] = [];
        practPinsRef.current.forEach((p) => {
          const [lat, lon] = p.l;


          const point = turf.point([Number(lon), Number(lat)]);
          const isInside = turf.booleanPointInPolygon(point, polygon as any);
          if (isInside) {
            if (loadedPractsIdsRef.current.includes(p.i)) {
              const pract = store.practitionersFilteredByFMC.find((ps) => ps.id === p.i)
              if (pract && !selectedPractitioners.find((sp) => pract.id === sp.id)) {
                selectedPractitioners = [
                  ...selectedPractitioners,
                  pract,
                ];
              }
            } else {
              notLoadedPracts.push(p.i);
            }
          }

        });

        handleMultiplePractitionersSelection(notLoadedPracts)
          .then((ps) => {
            selectedPractitioners = [
              ...selectedPractitioners,
              ...ps,
            ];
            selectedPractitioners?.forEach((sp: any) => {
              handleAddToRoute(sp);
            });
            if (selectedPractitioners.length > originalLength && setHasRouteChangedRef.current) {
              setHasRouteChangedRef.current(true);
            }
            setIsSlideUpMenusOpen(true);
          })
      } else {
        if (e.type === 'draw.delete') {
          store.setSelectedPractitioners([]);
          setRouteData(null);
        }
      }
    }

    const mountMapboxDraw = () => {
      if (!mapRef.current) return;

      console.log('Creating draw instance');
      drawRef.current = new MapboxDraw({
        controls: {
          polygon: false,
          trash: false,
          point: false,
          combine_features: false,
          uncombine_features: false,
          line_string: false,
        },
        modes: {
          ...MapboxDraw.modes,
          draw_polygon: FreehandMode,
        },

      });

      mapRef.current.addControl(drawRef.current);

      const map = mapRef.current;
      map.on('draw.create', updateArea);
      map.on('draw.delete', updateArea);
      map.on('draw.update', updateArea);



      return () => {
        if (mapRef.current && drawRef.current) {
          const map = mapRef.current;
          map.off('draw.create', updateArea);
          map.off('draw.delete', updateArea);
          map.off('draw.update', updateArea);
          map.removeControl(drawRef.current);
        }
      };
    };

    // Prevent pull-to-refresh when draw mode is active
    React.useEffect(() => {
      if (mapRef.current) {
        const map = mapRef.current;
        map.on('draw.modechange', (e) => {
          const mapCanvas = map.getCanvas();
          // If entering a draw mode
          if ((e as any).mode.startsWith('draw')) {
            document.body.style.overscrollBehavior = 'none';
            mapCanvas.style.touchAction = 'none';
          } else {
            // Reset when exiting draw mode
            document.body.style.overscrollBehavior = 'auto';
            mapCanvas.style.touchAction = 'auto';
            drawRef.current.deleteAll();
          }
        });
      }
    }, [mapRef.current]);

    const startDrawing = () => {
      if (!mapRef.current || !drawRef.current) {
        console.warn('Map or draw controls not initialized');
        return;
      }

      drawRef.current.deleteAll();

      if (!isLassoMode) {
        drawRef.current.changeMode('draw_polygon');
      } else {
        drawRef.current.changeMode('simple_select');
      }

      setIsLassoMode(!isLassoMode);
    };

    const centralizeMap = () => {
      if (geolocateControlRef.current) {
        geolocateControlRef.current.trigger();
      }
    }

    const handleRoutes = (opt: string) => {
      if (!selectedPractitioner) return;

      const practitioner = selectedPractitioner;
      const address = encodeURIComponent(practitioner.address || '');
      const coordinates = `${practitioner.latitude},${practitioner.longitude}`;
      const query = address || coordinates;

      switch (opt) {
        case 'google':
          window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
          break;
        case 'waze':
          window.open(`https://waze.com/ul?ll=${practitioner.latitude},${practitioner.longitude}&navigate=yes`, '_blank');
          break;
        case 'apple':
          window.open(`maps://?q=${query}`, '_blank');
          break;
      }
      setIsGoRouteModalOpen(false);
    };

    const handleAddToRoute = (practitioner: any) => {
      store.addToRoute(practitioner);
      if (setHasRouteChangedRef.current) {
        setHasRouteChangedRef.current(true);
      }
      setSelectedPractitioner(undefined);
      setIsSlideUpMenusOpen(true);
    };

    return (
      <div className={styles.container}>
        <LoadingOverlay
          visible={(!isMapLoaded || isPractitionerListLoading)}
          zIndex={1000} />
        {selectedPractitioner && (
          <div className={styles.focusedPractitionerContainer}>
            <div className={styles.focusedPractitioner}>
              <div >
                <div className={styles.practitionerName}>{selectedPractitioner?.name || selectedPractitioner?.primary_name}</div>
                <div className={styles.subInfo}>{selectedPractitioner?.address}</div>
                <div className={styles.subInfo}>{selectedPractitioner?.specialty}</div>
              </div>
              <div className={styles.iconsContainer}>
                <div className={styles.blueIdentificationCard} onClick={() => {
                  store.setCurrentPractitioner(selectedPractitioner);
                  setIsSlideUpMenusOpen(true);
                }}>
                  <IdentificationIcon />
                </div>
                <div onClick={() => setIsGoRouteModalOpen(true)}>
                  <NavigationArrowIcon stroke={'#4F7D9C'} width="24px" height="24px" />
                </div>
                <div onClick={() => handleAddToRoute(selectedPractitioner)} className={styles.addRouteIcon}>
                  <AddRouteIcon />
                </div>
              </div>
            </div>

          </div>
        )}
        <SlideUpMenus
          isOpen={isSlideUpMenusOpen}
          selectedView={selectedPractitioner ? 'baseballcard' : 'createRoute'}
          setIsOpen={setIsSlideUpMenusOpen}
          onRouteChanged={(setHasRouteChanged) => {
            setHasRouteChangedRef.current = setHasRouteChanged;
          }}
        />
        <RoutesTab isOpen={isGoRouteModalOpen} setIsOpen={setIsGoRouteModalOpen} onSelect={handleRoutes} />
        <div className={styles.container}>
          <RoutingModal
            selectedPractitioners={store.selectedPractitioners}
            isOpen={isRoutingModalOpen}
            onClear={() => {
              store.setSelectedPractitioners([]);
              setIsRoutingModalOpen(false);
            }}
            handleClose={() => setIsRoutingModalOpen(false)}
          />
          {initialLatLon && (<Map
            ref={mapRef as any}
            style={{
              position: 'absolute',
              width: '100vw',
              height: '100%',
              top: '0',
              left: '0'
            }}
            onTouchStart={(e) => {
              setTouchStart({
                x: e.point.x,
                y: e.point.y,
                time: Date.now()
              });
            }}
            onTouchEnd={(e) => {
              if (!touchStart) return;

              const touchEnd = {
                x: e.point.x,
                y: e.point.y,
                time: Date.now()
              };

              const distance = Math.sqrt(
                Math.pow(touchEnd.x - touchStart.x, 2) +
                Math.pow(touchEnd.y - touchStart.y, 2)
              );
              const duration = touchEnd.time - touchStart.time;

              if (duration < 300 && distance < 10) {
                const [featureEvent,] = e.target.queryRenderedFeatures(e.point);

                if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
                  e.preventDefault();
                  const clusterId = featureEvent.properties?.cluster_id;
                  const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

                  if (source && clusterId !== undefined && mapRef.current) {
                    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                      if (err || !mapRef.current) return;

                      const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
                      const targetZoom = Math.max(12, (zoom || 10) + 1);

                      mapRef.current.flyTo({
                        center: [coordinates[0], coordinates[1]],
                        zoom: targetZoom,
                        speed: 2,
                        curve: 1.5,
                        bearing: 0,
                        pitch: 0,
                        essential: true,
                      });
                    });
                  }
                  return;
                }

                // Only reset state if not clicking on cluster
                setIsPopupOpen(false);
                setSelectedPractitioner(undefined);

                if (featureEvent && (featureEvent.layer?.id === "unclustered-point" || featureEvent.layer?.id === "route-points")) {
                  handlePractitionerSelection(featureEvent?.properties?.id)
                    .then((p) => setSelectedPractitioner(p));
                } else {
                  setSelectedPractitioner(false);
                }
              }

              setTouchStart(null);
            }}
            onClick={(e) => {
              const [featureEvent,] = e.target.queryRenderedFeatures(e.point);

              // Handle cluster clicks first - check both cluster layers
              if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
                e.preventDefault();
                const clusterId = featureEvent.properties?.cluster_id;
                const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

                if (source && clusterId !== undefined && mapRef.current) {
                  source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err || !mapRef.current) return;

                    const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
                    const targetZoom = Math.max(12, (zoom || 10) + 1);

                    mapRef.current.flyTo({
                      center: [coordinates[0], coordinates[1]],
                      zoom: targetZoom,
                      speed: 2,
                      curve: 1.5,
                      bearing: 0,
                      pitch: 0,
                      essential: true,
                    });
                  });
                }
                return;
              }

              // Only reset state if not clicking on cluster
              setIsPopupOpen(false);
              setSelectedPractitioner(undefined);
              if (featureEvent && ((featureEvent.layer?.id === "unclustered-point") || (featureEvent.layer?.id === "route-points"))) {
                handlePractitionerSelection(featureEvent?.properties?.id)
                  .then((p) => setSelectedPractitioner(p));
              } else {
                setSelectedPractitioner(false);
              }
            }}
            onLoad={() => {
              console.log('Map loaded');
              //image 
              mapRef?.current?.loadImage("/icons/red-marker.png", (err, img) => {
                if (err || !img) throw err;
                console.log("image loaded")
                mapRef?.current?.addImage('custom-marker', img);
              });
              mapRef?.current?.loadImage("/icons/route-marker.png", (err, img) => {
                if (err || !img) throw err;
                console.log("image loaded")
                mapRef?.current?.addImage('custom-marker-route', img);
              });
              mapRef?.current?.loadImage("/icons/selected-marker.png", (err, img) => {
                if (err || !img) throw err;
                console.log("image loaded")
                mapRef?.current?.addImage('selected-marker', img);
              });
              setIsMapLoaded(true);
            }}
            initialViewState={{
              /* SF
              longitude: -122.60,
              latitude: 37.792,
              */
              longitude: initialLatLon[1],
              latitude: initialLatLon[0],
              zoom: focalZoom, //11.8,
              bearing: 0, //100,
              pitch: 0, //65,
            }}
            onData={(e: any) => {
              if (e?.sourceId === "practitioners-source") {
                const map = mapRef.current;
                const features = map?.querySourceFeatures("practitioners-source", {
                  sourceLayer: "unclustered-points",
                });
                const idsToFetch: number[] = [];
                features?.forEach((f) => {
                  if (!f?.properties?.point_count) {
                    //filter this by already loaded an trigger request by ids,]
                    //accumulate results on practitioners
                    //fill store.FMC with practitioners by using useEffect

                    if (f?.properties?.id && !idsToFetch.includes(f?.properties?.id)) {
                      idsToFetch.push(f.properties.id);
                    }
                  }
                });
                if (idsToFetch.length > 0) {
                  setOnViewPractsIds(idsToFetch);
                }
              }
            }}
            mapStyle="mapbox://styles/mapbox/standard"
            mapboxAccessToken={mapboxToken}
          >
            {isMapLoaded && (
              <Source
                id="practitioners-source"
                type="geojson"
                data={practitionersGeoJSON()}
                cluster={true}
                clusterMaxZoom={14}
                clusterRadius={50}
              >
                {/* 8d-i) Cluster circles when zoomed out */}
                <Layer
                  id="clusters"
                  type="circle"
                  filter={['has', 'point_count']}
                  paint={{
                    'circle-color': [
                      'step',
                      ['get', 'point_count'],
                      '#51bbd6',
                      100,
                      '#f1f075',
                      750,
                      '#f28cb1'
                    ],
                    'circle-radius': [
                      'step',
                      ['get', 'point_count'],
                      20,
                      100,
                      30,
                      750,
                      40
                    ],
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffffff'
                  }}
                />

                {/* 8d-ii) Cluster count labels */}
                <Layer
                  id="cluster-count"
                  type="symbol"
                  filter={['has', 'point_count']}
                  layout={{
                    'text-field': '{point_count_abbreviated}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12
                  }}
                  paint={{
                    'text-color': '#000000'
                  }}
                />

                {/* 8d-iii) Individual (unclustered) practitioner circles */}
                <Layer
                  id="unclustered-point"
                  type="symbol"
                  filter={[
                    'all',
                    ['!', ['has', 'point_count']],
                  ]}
                  layout={{
                    'icon-image': "custom-marker",
                    'icon-size': .8,
                    "icon-allow-overlap": true,
                  }}
                />
                <Layer
                  id="selected-point"
                  type="symbol"
                  filter={[
                    '==', ['get', 'id'], selectedPractitioner?.id,
                  ]}
                  layout={{
                    'icon-image': "selected-marker",
                    'icon-size': .9,
                    "icon-allow-overlap": true,
                  }}
                />
                
                {/* <Layer
                  id="route-points"
                  type="symbol"
                  filter={[
                    'all',
                    ['==', ['get', 'selected'], true],
                    ['!', ['in', ['get', 'id'], ['literal', store?.selectedPractitioners.filter(p => p.isFirstLocation || p.isLastLocation).map(p => p.id) || []]]]
                  ]}
                  layout={{
                    'text-field': [
                      "number-format",
                      ["+",
                        ["index-of",
                          ["get", "id"],
                          ["literal", store?.selectedPractitioners.filter(p => !p.isFirstLocation && !p.isLastLocation).map((s) => s.id) || []]],
                        store?.selectedPractitioners.some(p => p.isFirstLocation) ? 2 : 1,
                      ],
                      {},
                    ],
                    'icon-image': "custom-marker-route",
                    'icon-size': .8,
                    "icon-allow-overlap": false,
                  }}
                  paint={{
                    'text-color': '#fff'
                  }}
                /> */}

              </Source>
            )}
            {(isMapLoaded && (routePinsSource?.features?.length || 0) > 0) && (
              <Source
                id="route-source"
                type="geojson"
                cluster={false}
                data={routePinsSource}>
                <Layer
                  id="route-points"
                  type="symbol"
                  filter={[
                    'all',
                  ]}
                  layout={{
                    'text-field': [
                      "number-format",
                      ["+",
                        ["index-of",
                          ["get", "id"],
                          ["literal", store?.selectedPractitioners
                            .filter((s) => !s.isFirstLocation && !s.isLastLocation)
                            .map((s) => s.id) || []]],
                        store?.selectedPractitioners.some(p => p.isFirstLocation) ? 2 : 1,
                      ],
                      {},
                    ],
                    'icon-image': "custom-marker-route",
                    'icon-size': [
                      'interpolate', ['linear'], ['zoom'],
                      4, 0.1,
                      14, 1.5
                    ],
                    "icon-allow-overlap": true,
                  }}
                  paint={{
                    'text-color': '#fff'
                  }}
                />
              </Source>
            )}

            {/* Add new source and layers for start/end locations */}
            {isMapLoaded && (
              <Source
                id="start-end-locations-source"
                type="geojson"
                data={startEndLocationsGeoJSON()}
              >
                {/* Start location markers */}
                <Layer
                  id="start-location-points"
                  type="symbol"
                  filter={['==', ['get', 'type'], 'start']}
                  layout={{
                    'text-field': '1',
                    'icon-image': "custom-marker-route",
                    'icon-size': 1,
                    "icon-allow-overlap": true,
                  }}
                  paint={{
                    'text-color': '#fff'
                  }}
                />

                {/* End location markers */}
                <Layer
                  id="end-location-points"
                  type="symbol"
                  filter={['==', ['get', 'type'], 'end']}
                  layout={{
                    'text-field': String(store?.selectedPractitioners.length),
                    'icon-image': "custom-marker-route",
                    'icon-size': 1,
                    "icon-allow-overlap": true,
                  }}
                  paint={{
                    'text-color': '#fff'
                  }}
                />
              </Source>
            )}



            {routeData && isMapLoaded && (
              <Source id="route" type="geojson" data={routeData}>
                <Layer
                  id="route-line"
                  type="line"
                  layout={{
                    'line-join': 'round',
                    'line-cap': 'round'
                  }}
                  paint={{
                    'line-color': '#0066FF',
                    'line-width': 4
                  }}
                />
              </Source>
            )}
          </Map>
          )}
          <div
            className={styles.toolBox}
          >
            <div>
              <>
                <div>
                  <div className={styles.buttonsContainer}>
                    <LocationIcon style={{ cursor: 'pointer' }} onClick={centralizeMap} width='35px' height='35px' />
                    <LassoIcon style={{ stroke: isLassoMode ? 'lightgreen' : 'white', cursor: 'pointer' }} onClick={startDrawing} width='35px' height='35px' />
                    <RoutesIcon style={{ color: "white" }}
                      onClick={() => {
                        setSelectedPractitioner(undefined);
                        setIsSlideUpMenusOpen(true);
                      }} width='35px' height='35px' />
                  </div>
                </div>
              </>
            </div>
          </div>
          <div className={styles.floatingBtnWrapper}>
            <ReturnIcon fill='white' style={{ cursor: 'pointer' }} onClick={navigateHome} width='30px' height='30px' />
          </div>
        </div>
      </div>
    );
  }
)

export default MapComponent;